import { ApolloProvider, useQuery } from '@apollo/client'
import { Base64 } from 'js-base64'
import { filter, get } from 'lodash'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'

import API from 'api'
import { getAppRoute } from 'helpers/application'
import { getMonthlyPlanDetailsUrl } from 'models/monthly'
import {
  getCurrentOrNextAvailability,
  getMultiparkingCanonicalURL,
} from 'models/onDemand/lots'
import { getPayBySignageUrl } from 'models/payBySignage'
import parseLotSlug from 'helpers/parseLotSlug'

import { useApollo } from '@/graphql/apolloClient'
import getLotQuery from '@/graphql/multiparking/queries/lot'
import lotParkingMethodsQuery from '@/graphql/multiparking/queries/lotParkingMethods'

const WithMultiparking = WrapperComponent => {
  const Wrapper = props => {
    const router = useRouter()
    const { application, slug } = router.query
    const lotId = parseLotSlug(slug)

    const [isLoading, setIsLoading] = useState(true)
    const [lotRestData, setLotRestData] = useState(null)
    const [parkingMethodsState, setParkingMethodsState] = useState(null)
    const [isClosed, setIsClosed] = useState(false)
    const [downloadAppLink, setDownloadAppLink] = useState('/download-app')

    const client = useApollo(props)

    const { data: lotGqlData, error: lotGqlError } = useQuery(getLotQuery, {
      variables: { id: lotId },
      skip: !lotId,
      client,
    })

    const { data: parkingMethodsData, error: parkingMethodsError } = useQuery(
      lotParkingMethodsQuery,
      {
        variables: { id: lotId },
        skip: !lotId || isClosed,
        client,
      }
    )

    useEffect(() => {
      if (!lotId) return

      const fetchRestData = async () => {
        try {
          const response = await API().getLot(lotId)
          const lot = response?.lot

          if (!lot || lot?.error) {
            window.location.replace('/park')

            return
          }

          setLotRestData(lot)

          if (!lot.isVisible) {
            setIsClosed(true)

            const link = await API()
              .magicLink({
                body: JSON.stringify({ sendTextMessage: false }),
              })
              .then(resp =>
                !resp.error ? resp.magicLink?.url : '/download-app'
              )
              .catch(() => '/download-app')

            setDownloadAppLink(link)
          }
        } catch {
          window.location.replace('/park')
        }
      }

      fetchRestData()
    }, [lotId])

    useEffect(() => {
      if (!lotRestData || !lotGqlData?.lot) return
      if (isClosed) {
        setIsLoading(false)

        return
      }
      if (!parkingMethodsData?.lotParkingMethods) return

      const { event, onDemand, subscription } =
        parkingMethodsData.lotParkingMethods

      const upcomingAvailabilities = lotRestData.upcomingAvailabilities || []
      const availabilities = getCurrentOrNextAvailability({
        upcomingAvailabilities,
      })
      const isVariableRate = filter(
        availabilities,
        availability => get(availability, 'rate.type') === 'variable'
      )

      const hasEvent = !!event?.active
      const hasOnDemand = !!onDemand?.active
      const hasOnDemandWithActiveRates =
        hasOnDemand && isVariableRate.length > 0
      const hasMonthly = !!subscription?.active

      const lot = lotGqlData.lot
      const onDemandLink = `${getAppRoute(
        application,
        'url'
      )}/parking-lots/${getPayBySignageUrl(lot)}/book`
      const eventLink = `${getAppRoute(
        application,
        'url'
      )}/events?lotId=${lotId}`
      const monthlyLink = getMonthlyPlanDetailsUrl({ lot })

      if (hasEvent && !hasOnDemandWithActiveRates && !hasMonthly) {
        window.location.replace(eventLink)

        return
      }

      if (hasOnDemand && !hasEvent && !hasMonthly) {
        window.location.replace(onDemandLink)

        return
      }

      if (hasMonthly && !hasEvent && !hasOnDemandWithActiveRates) {
        window.location.replace(monthlyLink)

        return
      }

      setParkingMethodsState({
        hasEvent,
        hasOnDemand: hasOnDemandWithActiveRates,
        hasMonthly,
        eventLink,
        onDemandLink,
        monthlyLink,
      })

      setIsLoading(false)
    }, [lotRestData, lotGqlData, parkingMethodsData, isClosed])

    if (lotGqlError || parkingMethodsError) {
      window.location.replace('/park')

      return null
    }

    const hasUserData = !!router.query.user_data
    const queryString =
      hasUserData && JSON.parse(Base64.decode(router.query.user_data))

    return (
      <ApolloProvider client={client}>
        <WrapperComponent
          isLoading={isLoading}
          {...props}
          {...(hasUserData && { queryString })}
          canonicalURL={
            lotGqlData?.lot
              ? getMultiparkingCanonicalURL(lotGqlData.lot)
              : undefined
          }
          downloadAppLink={downloadAppLink}
          isClosed={isClosed}
          lot={lotGqlData?.lot}
          openingHours={lotRestData?.openingHours}
          upcomingAvailabilities={lotRestData?.upcomingAvailabilities}
          {...parkingMethodsState}
        />
      </ApolloProvider>
    )
  }

  return Wrapper
}

export default WithMultiparking
